import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import {
  ENTERTAINMENT_ACTS_QUERY,
  CREATE_ENTERTAINMENT_ACT_MUTATION,
  UPDATE_ENTERTAINMENT_ACT_MUTATION,
  DELETE_ENTERTAINMENT_ACT_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const EMPTY_ACT = { name: '', actType: 'carnatic_orchestra', contactPerson: '', phone: '', email: '', description: '', minDurationHours: 1, pricePerHour: '', flatRate: '', city: '', isActive: true };

export default function AdminEntertainmentPage() {
  const [dialog, setDialog]     = useState(false);
  const [editAct, setEditAct]   = useState(null);
  const [form, setForm]         = useState(EMPTY_ACT);
  const [error, setError]       = useState('');

  const { data, loading, refetch } = useQuery(ENTERTAINMENT_ACTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const acts = data?.entertainmentActs || [];

  const { options: actTypeOpts } = useLov('act_type');

  const [createAct] = useMutation(CREATE_ENTERTAINMENT_ACT_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_ACT); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateAct] = useMutation(UPDATE_ENTERTAINMENT_ACT_MUTATION, {
    onCompleted: () => { setDialog(false); setEditAct(null); setForm(EMPTY_ACT); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteAct] = useMutation(DELETE_ENTERTAINMENT_ACT_MUTATION, { onCompleted: () => refetch() });

  const openCreate = () => { setEditAct(null); setForm(EMPTY_ACT); setDialog(true); };
  const openEdit   = (a) => {
    setEditAct(a);
    setForm({ name: a.name, actType: a.actType, contactPerson: a.contactPerson || '', phone: a.phone || '', email: a.email || '', description: a.description || '', minDurationHours: a.minDurationHours || 1, pricePerHour: a.pricePerHour || '', flatRate: a.flatRate || '', city: a.city || '', isActive: a.isActive !== false });
    setDialog(true);
  };
  const saveAct = () => {
    const vars = { ...form, minDurationHours: Number(form.minDurationHours) || 1, pricePerHour: form.pricePerHour ? Number(form.pricePerHour) : null, flatRate: form.flatRate ? Number(form.flatRate) : null };
    if (editAct) updateAct({ variables: { id: editAct.id, ...vars } });
    else createAct({ variables: vars });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Entertainment Admin</Typography>
          <Typography variant="body2" color="text.secondary">Manage entertainment acts available for ceremonies.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Act</Button>
      </Stack>

      <ContractedVendorsSection category="entertainment" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {acts.length === 0 && !loading && <Alert severity="info">No entertainment acts yet.</Alert>}

      <Grid container spacing={2}>
        <Paged items={acts}>{rows => rows.map(a => (
          <Grid item xs={12} sm={6} md={4} key={a.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <MusicNoteIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>{a.name}</Typography>
                    </Stack>
                    <Chip label={a.actType?.replace(/_/g,' ')} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
                    {a.city && <Typography variant="caption" display="block" color="text.secondary">{a.city}</Typography>}
                    {a.contactPerson && <Typography variant="caption" display="block" color="text.secondary">Contact: {a.contactPerson}</Typography>}
                    {a.phone && <Typography variant="caption" display="block" color="text.secondary">Phone: {a.phone}</Typography>}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {a.pricePerHour && <Chip label={`₹${a.pricePerHour}/hr`} size="small" color="success" />}
                      {a.flatRate && <Chip label={`₹${a.flatRate} flat`} size="small" color="info" />}
                      {a.minDurationHours && <Chip label={`Min ${a.minDurationHours}h`} size="small" variant="outlined" />}
                    </Stack>
                    {a.description && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{a.description}</Typography>}
                    {!a.isActive && <Chip label="Inactive" size="small" color="error" sx={{ mt: 1 }} />}
                  </Box>
                  <Stack direction="row">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteAct({ variables: { id: a.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAct ? 'Edit Entertainment Act' : 'New Entertainment Act'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Act / Group Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required autoFocus />
            <Stack direction="row" spacing={2}>
              <TextField select label="Act Type" value={form.actType} onChange={e => setForm(f => ({ ...f, actType: e.target.value }))} fullWidth>
                {actTypeOpts.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              <TextField label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Contact Person" value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} fullWidth />
              <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Min Duration (hours)" type="number" value={form.minDurationHours} onChange={e => setForm(f => ({ ...f, minDurationHours: e.target.value }))} fullWidth />
              <TextField label="₹ per Hour" type="number" value={form.pricePerHour} onChange={e => setForm(f => ({ ...f, pricePerHour: e.target.value }))} fullWidth />
              <TextField label="₹ Flat Rate" type="number" value={form.flatRate} onChange={e => setForm(f => ({ ...f, flatRate: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={saveAct} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
